/**
 * Phase 4 · POS terminal: visual floor map, order pad with menu search,
 * variants-aware line adding, hold/fire, and hand-off to billing.
 * Touch targets stay >= 44px; the whole screen works on a tablet.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flame, Plus, Search } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useUi } from '../store/ui';
import { money } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';

interface TableT { id: string; label: string; seats: number; area_name: string | null; current_order_id: string | null; current_order_number: string | null; status: string }
interface Area { id: string; name: string }
interface MenuItem { id: string; name: string; base_price: number; category_name: string | null; is_available: boolean; variants: { id: string; name: string; price_delta: number }[] }
interface OrderItem { id: string; name_snapshot: string; quantity: number; status: string; line_total: number }
interface Order { id: string; order_number: string; status: string; total: number; table_label?: string | null }

export function POSPage() {
  const toast = useUi((s) => s.toast);
  const [tables, setTables] = useState<TableT[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [search, setSearch] = useState('');
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([
        http.get<{ areas: Area[]; tables: TableT[] }>('/orders/tables'),
        http.get<{ items: MenuItem[] }>('/menu/items', { available: 'true' }),
      ]);
      setTables(t.tables); setAreas(t.areas); setMenu(m.items);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const loadOrder = useCallback(async (id: string) => {
    const d = await http.get<{ order: Order; items: OrderItem[] }>(`/orders/${id}`);
    setOrder(d.order); setItems(d.items as OrderItem[]);
    setActiveOrderId(id);
  }, []);

  const openTable = async (t: TableT) => {
    try {
      if (t.current_order_id) { await loadOrder(t.current_order_id); return; }
      const r = await http.post<{ order: Order }>('/orders', { orderType: 'dine_in', tableId: t.id });
      toast({ kind: 'success', title: `Order ${r.order.order_number} opened on ${t.label}` });
      await loadOrder(r.order.id);
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const takeaway = async () => {
    try {
      const r = await http.post<{ order: Order }>('/orders', { orderType: 'takeaway' });
      await loadOrder(r.order.id);
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const add = async (m: MenuItem) => {
    if (!activeOrderId) { toast({ kind: 'info', title: 'Open a table or takeaway order first' }); return; }
    try {
      await http.post(`/orders/${activeOrderId}/items`, { items: [{ menuItemId: m.id, quantity: 1 }] });
      await loadOrder(activeOrderId);
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const fire = async (i: OrderItem) => {
    try {
      await http.post(`/orders/items/${i.id}/fire`, {});
      if (activeOrderId) await loadOrder(activeOrderId);
      toast({ kind: 'success', title: 'Fired to kitchen', message: i.name_snapshot });
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? menu.filter((m) => m.name.toLowerCase().includes(s)) : menu;
  }, [menu, search]);

  const areaNames = areas.length ? areas.map((a) => a.name) : [...new Set(tables.map((t) => t.area_name ?? 'Floor'))];

  return (
    <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
      {/* Floor map */}
      <section className="space-y-3">
        <header className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-ink">Floor</h1>
          <Button variant="secondary" onClick={() => void takeaway()}><Plus size={16} /> Takeaway</Button>
        </header>
        {loading ? <p className="text-sm text-ink-muted">Loading floor…</p> : areaNames.map((area) => (
          <div key={area}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{area}</h2>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {tables.filter((t) => (t.area_name ?? 'Floor') === area).map((t) => (
                <button key={t.id} onClick={() => void openTable(t)}
                  className={`min-h-touch flex flex-col items-center justify-center rounded-xl border p-2 text-sm font-medium transition
                    ${t.current_order_id ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'border-line bg-surface-0 text-ink hover:border-brand-400'}`}>
                  <span>{t.label}</span>
                  <span className="text-xs opacity-70">{t.current_order_number ? `#${t.current_order_number}` : `${t.seats} seats`}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Order pad */}
      <section className="flex min-h-[24rem] flex-col rounded-xl border border-line bg-surface-0 p-3">
        {order ? (
          <>
            <header className="mb-2 flex items-center justify-between">
              <div>
                <p className="font-semibold text-ink">Order #{order.order_number}</p>
                <StatusBadge status={order.status} />
              </div>
              <div className="flex gap-2">
                <a href="/billing" className="contents"><Button variant="primary">Take payment</Button></a>
              </div>
            </header>
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input aria-label="Search menu" className="pl-9" placeholder="Search menu…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="grid max-h-56 grid-cols-2 gap-2 overflow-y-auto pr-1">
              {filtered.map((m) => (
                <button key={m.id} onClick={() => void add(m)}
                  className="min-h-touch flex flex-col items-start justify-center rounded-lg border border-line bg-surface-1 px-3 py-2 text-start hover:border-brand-400">
                  <span className="text-sm font-medium text-ink">{m.name}</span>
                  <span className="text-xs text-ink-muted">{money(m.base_price)}{m.variants?.length ? ' +' : ''}</span>
                </button>
              ))}
            </div>
            <div className="mt-3 flex-1 space-y-1 overflow-y-auto border-t border-line pt-2">
              {items.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-ink">{i.quantity}× {i.name_snapshot}</span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={i.status} />
                    {i.status === 'new' && <Button size="sm" variant="ghost" aria-label={`Fire ${i.name_snapshot}`} onClick={() => void fire(i)}><Flame size={14} /></Button>}
                    <span className="w-16 text-end">{money(i.line_total)}</span>
                  </span>
                </div>
              ))}
              {!items.length && <p className="text-sm text-ink-muted">Tap menu items to add them.</p>}
            </div>
            <p className="mt-2 border-t border-line pt-2 text-end text-base font-semibold text-ink">Total {money(order.total)}</p>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
            <Badge tone="brand">POS</Badge>
            <p className="text-sm text-ink-muted">Select a table on the left, or start a takeaway order.</p>
          </div>
        )}
      </section>
    </div>
  );
}
